import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

import { ResumeUploadService } from './resume-upload.service';
import {
  ResumeConsentService,
  ResumeConsentStatus
} from './resume-consent.service';

interface PageContext {
  clientId: number;
  profileId: number;
  siteId: number;
  cookieValue: string;
  encryptedBruid: string;
  jobId: number;
  pageType: string;
}

export enum ResumeWorkflowResult {
  Completed = 'completed',
  UploadRequired = 'upload_required',
  Cancelled = 'cancelled'
}

@Injectable({
  providedIn: 'root'
})
export class ResumeConsentWorkflowService {

  private dialogElement: any = null;

  constructor(
    private resumeUploadService: ResumeUploadService,
    private consentService: ResumeConsentService,
    private translate: TranslateService,
    private http: HttpClient
  ) { }

  private getPageContext(overrideJobId?: number, overridePageType?: string): PageContext {
    const appScope = (window as any).appScope;
    const hash = window.location.hash.replace('#', '');
    const hashLower = hash.toLowerCase();

    let jobId = overrideJobId ?? 0;
    let pageType = overridePageType ?? 'jobDetail';

    if (overrideJobId) {
      // jobId and pageType both supplied by caller — no hash inspection needed
    } else if (hashLower.indexOf('jobdetail') !== -1) {
      jobId = Number(hash.split('=')[1]?.split('_')[0]) || 0;
      pageType = 'jobDetail';
    } else if (hashLower.indexOf('applypage') !== -1) {
      const applyUrl = appScope?.jobApplyUrl ?? '';
      const match = applyUrl.match(/[?&]reqid=(\d+)/i);
      jobId = match ? Number(match[1]) : 0;
      pageType = 'Apply';
    } else {
      // GQ apply flow — reqid is in the URL query string
      const urlParams = new URLSearchParams(window.location.search);
      jobId = Number(urlParams.get('reqid')) || 0;
      pageType = 'Apply';
    }

    return {
      clientId: Number((document.getElementById('partnerId') as HTMLInputElement)?.value),
      profileId: Number(appScope?.Abc),
      siteId: Number((document.getElementById('siteId') as HTMLInputElement)?.value),
      cookieValue: (document.getElementById('CookieValue') as HTMLInputElement)?.value ?? '',
      encryptedBruid: appScope?.encryptedBruid ?? '',
      jobId,
      pageType
    };
  }

  /**
   * Called from AngularJS on every Job Detail / Apply page load
   */
  async start(jobId?: number, pageType?: string): Promise<ResumeWorkflowResult> {

    const ctx = this.getPageContext(jobId, pageType);
    if (!ctx.jobId) return ResumeWorkflowResult.Cancelled;

    const sessionKey = ctx.encryptedBruid || await this.fetchEncryptedBruid(ctx.profileId);

    const currentStatus =
      this.consentService.getStatus(
        ctx.clientId,
        sessionKey
      );

    switch (currentStatus) {

      case ResumeConsentStatus.Confirmed:
        this.loadProfileMatchWidget(ctx);
        return ResumeWorkflowResult.Completed;

      case ResumeConsentStatus.Initial:
      case ResumeConsentStatus.Denied:
        return ResumeWorkflowResult.Cancelled;

      default:
        return await this.beginConsentFlow(ctx, sessionKey);
    }
  }

  private async fetchEncryptedBruid(profileId: number): Promise<string> {
    const res = await firstValueFrom(
      this.http.get<{ encryptedBruid: string }>(
        `/TGNewUI/Profile/Home/GetEncryptedBruid?profileId=${profileId}`
      )
    );
    return res.encryptedBruid;
  }

  /**
   * First dialog flow
   */
  private async beginConsentFlow(
    ctx: PageContext,
    sessionKey: string
  ): Promise<ResumeWorkflowResult> {

    this.consentService.saveStatus(
      ctx.clientId,
      sessionKey,
      ResumeConsentStatus.Denied
    );

    const proceed =
      await this.showInitialConsentDialog();

    if (!proceed) {
      return ResumeWorkflowResult.Cancelled;
    }

    this.consentService.saveStatus(
      ctx.clientId,
      sessionKey,
      ResumeConsentStatus.Initial
    );

    return await this.validateResume(ctx, sessionKey);
  }

  /**
   * Validation API
   */
  private async validateResume(
    ctx: PageContext,
    sessionKey: string
  ): Promise<ResumeWorkflowResult> {

    const response =
      await firstValueFrom(
        this.resumeUploadService.submitConsent(
          ctx.profileId,
          ctx.clientId
        )
      );

    switch (response) {

      case 1:
        return await this.handleOldResume(ctx, sessionKey);

      case 2:
        return ResumeWorkflowResult.UploadRequired;

      default:

        this.consentService.saveStatus(
          ctx.clientId,
          sessionKey,
          ResumeConsentStatus.Confirmed
        );

        this.loadProfileMatchWidget(ctx);

        return ResumeWorkflowResult.Completed;
    }
  }

  /**
   * Resume older than 90 days
   */
  private async handleOldResume(
    ctx: PageContext,
    sessionKey: string
  ): Promise<ResumeWorkflowResult> {

    this.consentService.saveStatus(
      ctx.clientId,
      sessionKey,
      ResumeConsentStatus.Initial
    );

    const useExisting =
      await this.showOldResumeDialog();

    if (!useExisting) {
      return ResumeWorkflowResult.UploadRequired;
    }

    await firstValueFrom(
      this.resumeUploadService.submitConsent(
        ctx.profileId,
        ctx.clientId,
        true
      )
    );

    this.consentService.saveStatus(
      ctx.clientId,
      sessionKey,
      ResumeConsentStatus.Confirmed
    );

    this.loadProfileMatchWidget(ctx);

    return ResumeWorkflowResult.Completed;
  }

  private showInitialConsentDialog(): Promise<boolean> {

    return new Promise(resolve => {

      const dialog =
        this.createDialog({
          isOpen: true,
          title: this.translate.instant('Resume_Analysis'),
          content: this.translate.instant('Resume_Consent_Message'),
          actions: [
            {
              id: 'cancel',
              label: this.translate.instant('Button_Cancel'),
              type: 'secondary'
            },
            {
              id: 'proceed',
              label: this.translate.instant('Dialog_Proceed'),
              type: 'primary'
            }
          ]
        });

      dialog.addEventListener(
        'action',
        (event: any) => {

          this.closeDialog();

          resolve(
            event.detail.actionId === 'proceed'
          );
        }
      );

      dialog.addEventListener(
        'close',
        () => {

          this.closeDialog();

          resolve(false);
        }
      );
    });
  }

  private showOldResumeDialog(): Promise<boolean> {

    return new Promise(resolve => {

      const dialog =
        this.createDialog({
          isOpen: true,
          title: this.translate.instant('Confirm_Resume'),
          content: this.translate.instant('Confirm_Resume_line1'),
          actions: [
            {
              id: 'upload_new',
              label: this.translate.instant('Dialog_UploadNew'),
              type: 'primary'
            },
            {
              id: 'use_existing',
              label: this.translate.instant('Dialog_UseExisting'),
              type: 'secondary'
            }
          ]
        });

      dialog.addEventListener(
        'action',
        (event: any) => {

          this.closeDialog();

          resolve(
            event.detail.actionId === 'use_existing'
          );
        }
      );

      dialog.addEventListener(
        'close',
        () => {

          this.closeDialog();

          resolve(false);
        }
      );
    });
  }

  private createDialog(
    config: any
  ): HTMLElement {

    const container =
      document.getElementById(
        'commonDialogContainer'
      );

    if (!container) {
      throw new Error(
        'commonDialogContainer not found'
      );
    }

    container.innerHTML = '';

    this.dialogElement =
      document.createElement(
        'app-common-warepper-dialogue'
      );

    (this.dialogElement as any).config =
      config;

    container.appendChild(
      this.dialogElement
    );

    document.body.classList.add(
      'noScroll'
    );

    return this.dialogElement;
  }

  private closeDialog(): void {

    const container =
      document.getElementById(
        'commonDialogContainer'
      );

    if (container) {
      container.innerHTML = '';
    }

    this.dialogElement = null;

    document.body.classList.remove(
      'noScroll'
    );
  }

  private loadProfileMatchWidget(ctx: PageContext): void {

    (window as any)
      .waitForJobDataAndLoadWidget(
        ctx.clientId,
        ctx.siteId,
        ctx.jobId,
        ctx.cookieValue,
        true,
        ctx.pageType,
        'ProfileMatch'
      );
  }
}
